import React, { useEffect, useState } from 'react';
import Learn from './tabs/Learn';
import Serve from './tabs/Serve';
import Impact from './tabs/Impact';
import Give from './tabs/Give';
import Services from './tabs/Services';
import Moments from './tabs/Moments';
import Activity from './tabs/Activity';
import Profile from './tabs/Profile';
import ChatInboxPanel from '../components/ChatInboxPanel';
import AppTopBar from '../components/AppTopBar';
import BottomNav from '../components/BottomNav';
import ScanSheet from '../components/ScanSheet';
import EntrySheet from '../components/EntrySheet';
import RewardsSheet from '../components/RewardsSheet';
import HomeViewSwitch from '../components/HomeViewSwitch';
import ComingSoonView from './tabs/ComingSoonView';
// Lazy: the tracker pulls in its own engine and catalogue, and Home is the
// landing view for members who never turn it on.
const TrackerHome = React.lazy(() => import('../features/tracker/TrackerHome'));
import { useOfferings } from '../components/OfferingsProvider';
import { chatSeeds } from '../data/mockData';
import { IHSAN_POINTS } from '../lib/rewards';
import { getMyProfile } from '../lib/pass';
import { useSession } from '../lib/session';
import { usePrefs, loadPrefs } from '../lib/prefs';

// Every destination the shell can render. `search` marks the one screen whose
// top bar holds the search field instead of a title; `legacy` marks the older
// pillar pages that predate the hub layout and are only reachable by deep link.
const SCREENS = {
  home: { component: Services, search: true },
  moments: { component: Moments, title: 'Moments' },
  activity: { component: Activity, title: 'Activity' },
  profile: { component: Profile, title: 'You', back: true },
  serve: { component: Serve, title: 'Serve', legacy: true },
  learn: { component: Learn, title: 'Learn', legacy: true },
  impact: { component: Impact, title: 'Impact', legacy: true },
  give: { component: Give, title: 'Give', legacy: true }
};

// The tab ids this shell used to have. Screens deep inside the app still ask
// for them by name, so they are translated rather than chased down and renamed.
const ALIASES = { explore: 'home', updates: 'activity', saved: 'profile', me: 'profile' };

// Add, Scan and Messages open over the current screen instead of replacing it,
// so a member returns to exactly where they were.
//
// Every bottom tab reopens on its own overview rather than resuming whichever
// filter or hub was last browsed.
const RESETS_TO_ALL = ['home', 'moments', 'activity', 'profile'];

export default function SuperApp() {
  const [activeTab, setActiveTab] = useState('home');
  const [resetKey, setResetKey] = useState(0);
  const [activeTopTab, setActiveTopTab] = useState('all');
  const [chatInboxOpen, setChatInboxOpen] = useState(false);
  const [scanOpen, setScanOpen] = useState(false);
  const [entryOpen, setEntryOpen] = useState(false);
  const [rewardsOpen, setRewardsOpen] = useState(false);
  // Live Ihsan Points for the top bar / RewardsSheet. Falls back to the
  // IHSAN_POINTS placeholder (0) until the profile fetch lands. Refetched
  // every time the RewardsSheet opens so the balance reflects what other
  // tabs (Learn enrol, Volunteer join) may have earned.
  const session = useSession();
  const [livePoints, setLivePoints] = useState(IHSAN_POINTS);
  useEffect(() => {
    if (!session) { setLivePoints(IHSAN_POINTS); return undefined; }
    let live = true;
    (async () => {
      try {
        const profile = await getMyProfile();
        if (live && profile && typeof profile.points === 'number') setLivePoints(profile.points);
      } catch { /* keep placeholder */ }
    })();
    return () => { live = false; };
  }, [session, rewardsOpen]);
  // Which segment Me opens on. Reset on a plain Profile visit so it never
  // sticks; "Log volunteer hours" in the entry sheet is the one caller that
  // asks for something other than All.
  const [profileMeTab, setProfileMeTab] = useState('all');
  const [pendingChatTarget, setPendingChatTarget] = useState(null);
  const [pendingExploreItem, setPendingExploreItem] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Which of the three Home views is showing. Seeded from the member's saved
  // default; changing it here is a look, not a commitment — "Make this my home"
  // is what changes what the app opens on.
  const { prefs, setPref } = usePrefs();
  const [homeView, setHomeView] = useState(prefs.home_view);
  const [homeViewTouched, setHomeViewTouched] = useState(false);

  useEffect(() => { loadPrefs(); }, []);

  // Follow the saved default until the member picks something in this session.
  useEffect(() => {
    if (!homeViewTouched) setHomeView(prefs.home_view);
  }, [prefs.home_view, homeViewTouched]);

  const { detailCollections } = useOfferings();
  const messageCount = Object.values(detailCollections)
    .flat()
    .filter((item) => chatSeeds[item.id]?.length).length;

  const screen = SCREENS[activeTab] || SCREENS.home;
  const ActiveComponent = screen.component;

  // Home's "nothing else going on" state: no hub chosen, nothing searched. The
  // view switch belongs only here — once someone has drilled into Give or typed
  // a search, offering to change their home screen is noise.
  const onHomeOverview = activeTab === 'home' && activeTopTab === 'all' && !searchQuery;
  const showAlternateHome = onHomeOverview && homeView !== 'services';

  const handleNavClick = (rawId) => {
    const tabId = ALIASES[rawId] || rawId;

    if (tabId === 'add') {
      setEntryOpen(true);
      return;
    }
    // Still honoured so any deep caller that asks for the scanner by name keeps
    // working now that the bottom-bar slot is "add".
    if (tabId === 'scan') {
      setScanOpen(true);
      return;
    }
    if (tabId === 'messages') {
      setChatInboxOpen(true);
      return;
    }
    // Me, opened straight onto its volunteering segment.
    if (tabId === 'volunteer-hours') {
      setProfileMeTab('volunteer');
      setActiveTopTab('all');
      setResetKey((k) => k + 1);
      setSearchQuery('');
      setActiveTab('profile');
      return;
    }
    if (!SCREENS[tabId]) return;

    if (tabId === 'profile') setProfileMeTab('all');
    // Home reopens on whichever view the member chose as their default, not
    // whatever they were last looking at.
    if (tabId === 'home') setHomeViewTouched(false);
    if (RESETS_TO_ALL.includes(tabId)) setActiveTopTab('all');
    if (tabId === activeTab || RESETS_TO_ALL.includes(tabId)) setResetKey((k) => k + 1);
    setSearchQuery('');
    setActiveTab(tabId);
  };

  const handleOpenExploreItem = (payload) => {
    setPendingExploreItem(payload);
    setActiveTopTab('all');
    setResetKey((k) => k + 1);
    setSearchQuery('');
    setActiveTab('home');
  };

  const handleSelectChat = (entry) => {
    setChatInboxOpen(false);
    setActiveTopTab(entry.type);
    setPendingChatTarget({ id: entry.id, type: entry.type });
    setActiveTab('home');
  };

  // Open Home on a particular hub. Deliberately not routed through
  // handleNavClick: that resets Home's top-tab to "all", which would undo the
  // hub we are trying to select.
  const handleOpenHub = (hub) => {
    setSearchQuery('');
    setActiveTab('home');
    setActiveTopTab(hub);
  };

  // The Schedule-a-Giving shortcut lands on the Give hub inside Home.
  const handleScheduleGiving = () => handleOpenHub('give');

  // Jump straight to the tracker (from the "+" sheet).
  const handleOpenTracker = () => {
    setSearchQuery('');
    setActiveTopTab('all');
    setActiveTab('home');
    setHomeView('tracker');
    setHomeViewTouched(true);
  };

  return (
    <div className="flex flex-col h-full">
      <main className="flex-1 overflow-y-auto pb-20">
        <AppTopBar
          title={screen.title}
          searchValue={screen.search ? searchQuery : undefined}
          onSearchChange={screen.search ? setSearchQuery : undefined}
          onOpenScan={() => setScanOpen(true)}
          onOpenProfile={() => handleNavClick('profile')}
          onBack={screen.back || screen.legacy ? () => handleNavClick('home') : undefined}
        />

        {onHomeOverview && (
          <HomeViewSwitch
            value={homeView}
            defaultView={prefs.home_view}
            onChange={(next) => { setHomeView(next); setHomeViewTouched(true); }}
            onMakeDefault={(next) => { setPref('home_view', next); setHomeViewTouched(false); }}
          />
        )}

        {showAlternateHome ? (
          homeView === 'tracker' ? (
            <React.Suspense
              fallback={<div className="mx-4 mt-6 h-28 rounded-2xl bg-muted/50 animate-pulse" />}
            >
              <TrackerHome />
            </React.Suspense>
          ) : (
            <ComingSoonView view={homeView} />
          )
        ) : ActiveComponent && (
          <ActiveComponent
            key={`${activeTab}-${resetKey}`}
            activeTab={activeTab}
            onNavigate={handleNavClick}
            activeTopTab={activeTopTab}
            onTopTabChange={setActiveTopTab}
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            onOpenRewards={() => setRewardsOpen(true)}
            onScheduleGiving={handleScheduleGiving}
            onOpenChatInbox={() => setChatInboxOpen(true)}
            pendingChatTarget={activeTab === 'home' ? pendingChatTarget : null}
            onConsumePendingChatTarget={() => setPendingChatTarget(null)}
            pendingExploreItem={activeTab === 'home' ? pendingExploreItem : null}
            onConsumePendingExploreItem={() => setPendingExploreItem(null)}
            onOpenExploreItem={handleOpenExploreItem}
            onOpenTracker={handleOpenTracker}
            initialMeTab={profileMeTab}
          />
        )}
      </main>

      <BottomNav
        activeTab={chatInboxOpen ? 'messages' : activeTab}
        onNavigate={handleNavClick}
        messageCount={messageCount}
      />

      <ChatInboxPanel
        isOpen={chatInboxOpen}
        onClose={() => setChatInboxOpen(false)}
        onSelectChat={handleSelectChat}
      />

      <ScanSheet isOpen={scanOpen} onClose={() => setScanOpen(false)} />

      <EntrySheet
        isOpen={entryOpen}
        onClose={() => setEntryOpen(false)}
        onNavigate={handleNavClick}
        onOpenHub={handleOpenHub}
        onOpenTracker={handleOpenTracker}
      />

      <RewardsSheet
        isOpen={rewardsOpen}
        onClose={() => setRewardsOpen(false)}
        points={livePoints}
        onBalanceChange={(newBalance) => setLivePoints(newBalance)}
      />
    </div>
  );
}
